import type { GridConfig } from "@/types/grid";
import type { BulkImportPageSetupStatic } from "@/types/bulk-import";

export const isVariousManufacturer = ({ name }: { name?: string }): boolean => {
  return name?.toLowerCase() === "various";
};

export const getManufacturersPageConfig =
  (): BulkImportPageSetupStatic<ManufacturerSchema> => ({
    id: "manufacturers",
    pageTitle: "cksetup.imports.manufacturers.pageTitle",
    pageHeading: "cksetup.imports.manufacturers.heading",
    learnMoreTitle: "cksetup.imports.manufacturers.learnMoreTitle",
    learnMoreText: "cksetup.imports.manufacturers.learnMoreText",
    templateFileName: "cksetup.imports.manufacturers.templateName",
    templateWorksheetName: "tableColumns.manufacturers.worksheetName",
    tabTitle: "cksetup.imports.bulk.manufacturesHeading",
    tabDescription: "cksetup.imports.bulk.manufacturesDescription",
    imageSrc: "/static/images/bulk/manufacturers.png",
    validationSchema: manufacturerSchema,
  });

export const getManufacturersGridConfig = async (): Promise<
  GridConfig<ManufacturerSchema>
> => {
  return {
    computeItemFn: (rawData) => {
      if (isVariousManufacturer(rawData)) {
        return {
          ...rawData,
          $readonly: true,
        };
      }
      return rawData;
    },
    defaultRowData: {
      active: true,
    },
    columns: [
      {
        field: "name",
        title: "cksetup.imports.nameColumnLabel",
        helperText: "cksetup.imports.manufacturers.nameHelperText",
        editor: "text",
        placeholder: "cksetup.imports.enterName",
        isKey: true,
      },
      {
        field: "address1",
        title: "cksetup.imports.manufacturers.address1",
        helperText: "cksetup.imports.manufacturers.address1HelperText",
        editor: "text",
        placeholder: "cksetup.imports.manufacturers.enterAddressPlaceholder",
        minWidthPx: 140,
      },
      {
        field: "address2",
        title: "cksetup.imports.manufacturers.address2",
        helperText: "cksetup.imports.manufacturers.address2HelperText",
        editor: "text",
        placeholder: "cksetup.imports.manufacturers.enterAddressPlaceholder",
        required: false,
      },
      {
        field: "address3",
        title: "cksetup.imports.manufacturers.address3",
        helperText: "cksetup.imports.manufacturers.address3HelperText",
        editor: "text",
        placeholder: "cksetup.imports.manufacturers.enterAddressPlaceholder",
        required: false,
      },
      {
        field: "phone1",
        title: "cksetup.imports.manufacturers.phone1",
        helperText: "cksetup.imports.manufacturers.phone1HelperText",
        editor: "text",
        placeholder:
          "cksetup.imports.manufacturers.enterPhoneNumberPlaceholder",
      },
      {
        field: "phone2",
        title: "cksetup.imports.manufacturers.phone2",
        helperText: "cksetup.imports.manufacturers.phone2HelperText",
        editor: "text",
        placeholder:
          "cksetup.imports.manufacturers.enterPhoneNumberPlaceholder",
        required: false,
      },
      {
        field: "fax",
        title: "cksetup.imports.manufacturers.fax",
        helperText: "cksetup.imports.manufacturers.faxHelperText",
        editor: "text",
        placeholder: "cksetup.imports.enterNumber",
      },
      {
        field: "faxAttn",
        title: "cksetup.imports.manufacturers.faxAttn",
        helperText: "cksetup.imports.manufacturers.faxAttnHelperText",
        editor: "text",
        placeholder: "cksetup.imports.enterNumber",
        required: false,
      },
      {
        field: "website",
        title: "cksetup.imports.manufacturers.website",
        helperText: "cksetup.imports.manufacturers.websiteHelperText",
        editor: "text",
        placeholder: "cksetup.imports.manufacturers.enterWebsiteLink",
        required: false,
      },
      {
        field: "keywords",
        title: "cksetup.imports.manufacturers.keywords",
        helperText: "cksetup.imports.manufacturers.keywordsHelperText",
        editor: "text",
        placeholder: "cksetup.imports.manufacturers.enterKeywordPlaceholder",
        required: false,
      },
      {
        field: "note1",
        title: "cksetup.imports.manufacturers.note1",
        helperText: "cksetup.imports.manufacturers.note1HelperText",
        editor: "text",
        placeholder: "cksetup.imports.manufacturers.notePlaceholder",
      },
      {
        field: "contact1",
        title: "cksetup.imports.manufacturers.contact1",
        helperText: "cksetup.imports.manufacturers.contact1HelperText",
        editor: "text",
        placeholder:
          "cksetup.imports.manufacturers.enterContactNamePlaceholder",
        minWidthPx: 210,
      },
      {
        field: "title1",
        title: "cksetup.imports.manufacturers.title1",
        helperText: "cksetup.imports.manufacturers.title1HelperText",
        editor: "text",
        placeholder:
          "cksetup.imports.manufacturers.enterContactTitlePlaceholder",
        minWidthPx: 210,
      },
      {
        field: "email1",
        title: "cksetup.imports.manufacturers.email1",
        helperText: "cksetup.imports.manufacturers.email1HelperText",
        editor: "text",
        placeholder:
          "cksetup.imports.manufacturers.enterContactEmailPlaceholder",
        minWidthPx: 210,
      },
      {
        field: "contact2",
        title: "cksetup.imports.manufacturers.contact2",
        helperText: "cksetup.imports.manufacturers.contact2HelperText",
        editor: "text",
        placeholder:
          "cksetup.imports.manufacturers.enterContactNamePlaceholder",
        required: false,
        minWidthPx: 220,
      },

      {
        field: "title2",
        title: "cksetup.imports.manufacturers.title2",
        helperText: "cksetup.imports.manufacturers.title2HelperText",
        editor: "text",
        placeholder:
          "cksetup.imports.manufacturers.enterContactTitlePlaceholder",
        required: false,
        minWidthPx: 210,
      },
      {
        field: "email2",
        title: "cksetup.imports.manufacturers.email2",
        helperText: "cksetup.imports.manufacturers.email2HelperText",
        editor: "text",
        placeholder:
          "cksetup.imports.manufacturers.enterContactEmailPlaceholder",
        required: false,
        minWidthPx: 210,
      },
      {
        field: "corp",
        title: "cksetup.imports.manufacturers.corp",
        helperText: "cksetup.imports.manufacturers.corpHelperText",
        editor: "text",
        placeholder: "cksetup.imports.manufacturers.corpPlaceholder",
        minWidthPx: 150,
      },
      {
        field: "active",
        title: "cksetup.imports.activeColumnLabel",
        editor: "boolean",
        minWidthPx: 110,
      },
      {
        field: "currency",
        title: "cksetup.imports.manufacturers.currency",
        helperText: "cksetup.imports.manufacturers.currencyHelperText",
        editor: "select",
        placeholder: "formFields.select",
        editorProps: {
          items: [
            { label: "USD", value: "USD" },
            { label: "EUR", value: "EUR" },
            { label: "GBP", value: "GBP" },
          ],
        },
      },
    ],
  };
};
